import { useEffect, useRef, useState } from 'react';

// The exit animation begins this many ms before a step's dwell ends (from the
// design handoff), so the outgoing grid is leaving as the clock wraps.
const EXIT_LEAD_MS = 620;

export interface RotationState {
  index: number;
  // 0–1 elapsed through the current step's dwell (updated every frame).
  progress: number;
  isExiting: boolean;
}

/**
 * Drives rotation across the steps (each page of each date range) using a
 * requestAnimationFrame clock (smooth, frame-accurate progress for the rotation
 * bars). A single step is static (no rotation).
 *
 * With a `pinnedIndex` (the wallboard screenshot renderer captures one frame
 * per step) the clock is stopped on that step, with no exit animation.
 */
const useLeaderboardRotation = ({
  steps,
  pinnedIndex,
}: {
  steps: { dwellSeconds: number }[];
  pinnedIndex?: number;
}): RotationState => {
  const [state, setState] = useState<RotationState>({
    index: 0,
    progress: 0,
    isExiting: false,
  });
  const startRef = useRef<number>(Date.now());

  const durationsKey = steps.map((s) => s.dwellSeconds).join(',');

  useEffect(() => {
    if (pinnedIndex !== undefined) {
      setState({
        index: Math.min(
          Math.max(0, pinnedIndex),
          Math.max(0, steps.length - 1),
        ),
        progress: 0,
        isExiting: false,
      });
      return;
    }
    if (steps.length <= 1) {
      setState({ index: 0, progress: 1, isExiting: false });
      return;
    }
    if (typeof requestAnimationFrame === 'undefined') {
      return;
    }

    const durations = steps.map((s) => Math.max(1, s.dwellSeconds) * 1000);
    const total = durations.reduce((a, b) => a + b, 0);
    startRef.current = Date.now();
    let raf = 0;

    const loop = () => {
      let t = (Date.now() - startRef.current) % total;
      let idx = 0;
      while (idx < durations.length - 1 && t >= durations[idx]) {
        t -= durations[idx];
        idx += 1;
      }
      const dur = durations[idx];
      setState({
        index: idx,
        progress: Math.min(1, t / dur),
        isExiting: t > dur - EXIT_LEAD_MS,
      });
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => cancelAnimationFrame(raf);
    // durationsKey captures dwell changes; steps.length captures count changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [durationsKey, steps.length, pinnedIndex]);

  return state;
};

export default useLeaderboardRotation;
